/**
 * WalletPageSkeletons - Shape-matching skeletons for Wallet page
 *
 * CANON Compliance (5.3, 5.4):
 * - Skeleton shape MUST match final content shape (no CLS)
 * - role="status" + aria-live="polite" for accessibility
 * - Shimmer animation via animate-pulse
 */
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { semanticTokens } from "@packages/config/theme";

/**
 * AssetCardSkeleton
 *
 * Matches AssetCard structure:
 * - Circle icon left (w-14 h-14)
 * - Currency name + chain badge
 * - Amount (text-3xl)
 * - USD equivalent
 */
export function AssetCardSkeleton() {
  return (
    <Card
      className={cn(
        semanticTokens.bg.surface,
        "border border-slate-100",
        "shadow-sm",
        semanticTokens.radius.lg
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading asset"
      data-testid="asset-card-skeleton"
    >
      <CardContent className={cn(
        "flex items-start gap-6",
        "p-6 lg:p-8"
      )}>
        {/* Circle Icon */}
        <Skeleton className="w-14 h-14 lg:w-16 lg:h-16 rounded-full flex-shrink-0" />

        {/* Content */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          {/* Currency Name */}
          <Skeleton className="h-5 w-16" />
          {/* Chain Badge */}
          <Skeleton className="h-5 w-12 rounded-full" />
          {/* Amount - large */}
          <Skeleton className="h-10 w-32 mt-1" />
          {/* USD Equivalent */}
          <Skeleton className="h-4 w-20 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * TransactionCardSkeleton
 *
 * Matches TransactionCard structure:
 * - Circle icon + type + amount + status badge
 * - From/To details
 * - Date + fees footer
 */
export function TransactionCardSkeleton() {
  return (
    <Card
      className={cn(
        semanticTokens.bg.surface,
        "border border-slate-100",
        "shadow-sm",
        semanticTokens.radius.lg
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading transaction"
      data-testid="transaction-card-skeleton"
    >
      <CardContent className={cn(
        "flex flex-col gap-6",
        "p-6 lg:p-8"
      )}>
        {/* Header: Type & Status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Type Icon */}
            <Skeleton className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex-shrink-0" />

            {/* Type & Amount */}
            <div className="flex-1 min-w-0">
              <Skeleton className="h-5 w-20" /> {/* Type */}
              <Skeleton className="h-8 w-28 mt-2" /> {/* Amount */}
              <Skeleton className="h-4 w-16 mt-1" /> {/* USD */}
            </div>
          </div>

          {/* Status Badge */}
          <Skeleton className="h-6 w-20 rounded-full flex-shrink-0" />
        </div>

        {/* Details: Source/Destination */}
        <div className={cn(
          "flex flex-col gap-2 pt-2 border-t",
          semanticTokens.border.subtle
        )}>
          <div className="flex items-start gap-2">
            <Skeleton className="h-4 w-10" /> {/* From: */}
            <Skeleton className="h-4 w-32" /> {/* Value */}
          </div>
          <div className="flex items-start gap-2">
            <Skeleton className="h-4 w-10" /> {/* To: */}
            <Skeleton className="h-4 w-32" /> {/* Value */}
          </div>
        </div>

        {/* Footer */}
        <div className={cn(
          "flex items-center justify-between gap-3 pt-2 border-t",
          semanticTokens.border.subtle
        )}>
          {/* Date */}
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4 rounded" />
            <div className="flex flex-col gap-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>

          {/* Fees & Receipt */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end gap-1">
              <Skeleton className="h-3 w-8" />
              <Skeleton className="h-4 w-12" />
            </div>
            <Skeleton className="w-9 h-9 rounded-md" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * PortfolioHeaderSkeleton
 *
 * Matches PortfolioHeader structure:
 * - Total value (large)
 * - Change indicator
 * - Live indicator
 */
export function PortfolioHeaderSkeleton() {
  return (
    <div
      className="flex flex-col gap-4"
      role="status"
      aria-live="polite"
      aria-label="Loading portfolio"
      data-testid="portfolio-header-skeleton"
    >
      {/* Label */}
      <Skeleton className="h-4 w-24" />
      {/* Total Value - very large */}
      <Skeleton className="h-14 w-48" />
      {/* Change + Live indicator */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-4 w-16" />
      </div>
    </div>
  );
}

/**
 * BalanceCardSkeleton
 *
 * Matches BalanceCard structure:
 * - Header with title + action buttons
 * - PortfolioHeader
 * - Grid of AssetCards (2 columns)
 * - Quick stats row
 */
export function BalanceCardSkeleton() {
  return (
    <Card
      className={semanticTokens.motion.standard}
      role="status"
      aria-live="polite"
      aria-label="Loading wallet balance"
      data-testid="balance-card-skeleton"
    >
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Title */}
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded" />
            <Skeleton className="h-6 w-32" />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Skeleton className="h-11 w-28 rounded-md" />
            <Skeleton className="h-11 w-28 rounded-md" />
            <Skeleton className="h-11 w-28 rounded-md" />
          </div>
        </div>
      </CardHeader>

      <CardContent className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
        {/* Portfolio Header */}
        <PortfolioHeaderSkeleton />

        {/* Asset Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <AssetCardSkeleton />
          <AssetCardSkeleton />
        </div>

        {/* Quick Stats */}
        <div className={cn(
          "grid grid-cols-3 gap-4 pt-4 border-t",
          semanticTokens.border.default
        )}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="text-center flex flex-col gap-1">
              <Skeleton className="h-3 w-12 mx-auto" />
              <Skeleton className="h-5 w-16 mx-auto" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * TransactionHistorySkeleton
 *
 * Matches TransactionHistory structure:
 * - Header with title + export button
 * - Filters row
 * - Grid of TransactionCards (2 columns)
 */
export function TransactionHistorySkeleton() {
  return (
    <Card
      className={semanticTokens.motion.standard}
      role="status"
      aria-live="polite"
      aria-label="Loading transactions"
      data-testid="transaction-history-skeleton"
    >
      <CardContent className={cn("space-y-4", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
        {/* Header */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Skeleton className="h-11 flex-1 rounded-md" />
          <Skeleton className="h-11 w-32 rounded-md" />
          <Skeleton className="h-11 w-32 rounded-md" />
        </div>

        {/* Transaction Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {[1, 2, 3, 4].map((i) => (
            <TransactionCardSkeleton key={i} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * WalletPageSkeleton
 *
 * Full page skeleton for /dashboard/wallet
 * Matches the layout: BalanceCard + separator + Transactions section
 */
export function WalletPageSkeleton() {
  return (
    <div
      className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}
      role="status"
      aria-live="polite"
      aria-label="Loading wallet page"
      data-testid="wallet-page-skeleton"
    >
      {/* Balance Card */}
      <BalanceCardSkeleton />

      {/* Separator */}
      <div className={cn("border-t", semanticTokens.border.default)} />

      {/* Transactions Section */}
      <div>
        <Skeleton className="h-8 w-36 mb-6" /> {/* "Transactions" title */}
        <TransactionHistorySkeleton />
      </div>
    </div>
  );
}
